<script setup lang="tsx">
import { vOnClickOutside } from '@vueuse/components'
import type { SymbolItem } from '~/service/modules/public'
import type { Column } from '~/components/v/Table.vue'

const { currentSymbol, currentSymbolItem, symbolList, changeCurrentSymbol, currentTickerItem } = useSymbol()
const { symbolPrice } = useWs()
const { preference, updatePreference } = useUser()
const { isSigned } = useKey()

const currentSymbolBalanceItem = computed(() => symbolPrice.value?.[currentSymbol.value])
const currentPricePrecision = computed(() => +currentTickerItem.value?.quoteAssetPrecision - +currentTickerItem.value?.baseAssetPrecision || 0)

const showSymbolList = ref(false)
const showPreference = ref(false)
const isClosed = ref(false)
function toggleSymbol() {
  if (!showSymbolList.value && !isClosed.value) {
    showSymbolList.value = true
  }
}
const search = ref('')

function clearSearch() {
  search.value = ''
}

const percent24 = computed(() => {
  const lastPrice = +currentSymbolBalanceItem.value?.closePrice || 0
  const openPrice = +currentSymbolBalanceItem.value?.openPrice || 0
  if (openPrice !== 0) {
    return +(((lastPrice - openPrice) / openPrice) * 100).toFixed(2)
  }
  return 0
})

const dataList = computed(() => [
  // {
  //   icon: (
  //     <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  //       <path fill-rule="evenodd" clip-rule="evenodd" d="M8.00065 13.3334C10.9462 13.3334 13.334 10.9456 13.334 8.00004C13.334 5.05452 10.9462 2.66671 8.00065 2.66671C5.05513 2.66671 2.66732 5.05452 2.66732 8.00004C2.66732 10.9456 5.05513 13.3334 8.00065 13.3334ZM8.00065 14.6667C11.6825 14.6667 14.6673 11.6819 14.6673 8.00004C14.6673 4.31814 11.6825 1.33337 8.00065 1.33337C4.31875 1.33337 1.33398 4.31814 1.33398 8.00004C1.33398 11.6819 4.31875 14.6667 8.00065 14.6667Z" fill="#777E91" />
  //       <path d="M7.33398 4.66667C7.33398 4.29848 7.63246 4 8.00065 4C8.36884 4 8.66732 4.29848 8.66732 4.66667C9.77189 4.66667 10.6673 5.5621 10.6673 6.66667C10.6673 7.03486 10.3688 7.33333 10.0007 7.33333C9.63246 7.33333 9.33398 7.03486 9.33398 6.66667C9.33398 6.29848 9.03551 6 8.66732 6H7.16293C6.88921 6 6.66732 6.22189 6.66732 6.49561C6.66732 6.70894 6.80382 6.89833 7.0062 6.96579L9.41674 7.7693C10.1636 8.01825 10.6673 8.71716 10.6673 9.50439C10.6673 10.5145 9.84847 11.3333 8.83837 11.3333H8.66732C8.66732 11.7015 8.36884 12 8.00065 12C7.63246 12 7.33398 11.7015 7.33398 11.3333C6.22941 11.3333 5.33398 10.4379 5.33398 9.33333C5.33398 8.96514 5.63246 8.66667 6.00065 8.66667C6.36884 8.66667 6.66732 8.96514 6.66732 9.33333C6.66732 9.70152 6.96579 10 7.33398 10H8.83837C9.11209 10 9.33398 9.77811 9.33398 9.50439C9.33398 9.29106 9.19748 9.10167 8.9951 9.03421L6.58457 8.2307C5.83773 7.98176 5.33398 7.28284 5.33398 6.49561C5.33398 5.48551 6.15283 4.66667 7.16293 4.66667H7.33398Z" fill="#777E91" />
  //     </svg>),
  //   title: 'USD',
  //   value: '?',
  //   valueClass: 'text-green',
  // },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M11.6572 6.60606C11.9069 6.33551 11.89 5.91374 11.6195 5.664L9.2862 3.51015C9.03082 3.27442 8.6372 3.27442 8.38182 3.51015L6.04846 5.664C5.77792 5.91373 5.76104 6.3355 6.01078 6.60605C6.26051 6.8766 6.68228 6.89347 6.95283 6.64374L8.16734 5.52267L8.16734 12C8.16734 12.3682 8.46582 12.6667 8.83401 12.6667C9.2022 12.6667 9.50067 12.3682 9.50067 12L9.50067 5.52269L10.7151 6.64373C10.9857 6.89347 11.4074 6.8766 11.6572 6.60606Z" fill="#777E91" />
      </svg>),
    title: '24h High',
    value: `${formatNumber(currentSymbolBalanceItem.value?.highPrice, currentPricePrecision.value)}`,
    valueClass: '',
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M10.9892 9.39394C11.2389 9.66449 11.2221 10.0863 10.9515 10.336L8.61823 12.4898C8.36286 12.7256 7.96923 12.7256 7.71385 12.4899L5.38049 10.336C5.10995 10.0863 5.09307 9.6645 5.34281 9.39395C5.59254 9.1234 6.01431 9.10653 6.28486 9.35626L7.49937 10.4773L7.49937 3.99998C7.49937 3.63179 7.79785 3.33331 8.16604 3.33331C8.53423 3.33331 8.8327 3.63179 8.8327 3.99998L8.8327 10.4773L10.0472 9.35627C10.3177 9.10653 10.7395 9.1234 10.9892 9.39394Z" fill="#777E91" />
      </svg>),
    title: '24h Low',
    value: `${formatNumber(currentSymbolBalanceItem.value?.lowPrice, currentPricePrecision.value)}`,
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M1.83268 2C2.20087 2 2.49935 2.29848 2.49935 2.66667V12C2.49935 12.3682 2.79783 12.6667 3.16602 12.6667H15.166C15.5342 12.6667 15.8327 12.9651 15.8327 13.3333C15.8327 13.7015 15.5342 14 15.166 14H3.16602C2.06145 14 1.16602 13.1046 1.16602 12V2.66667C1.16602 2.29848 1.46449 2 1.83268 2Z" fill="#777E91" />
        <path d="M12.5007 3.33331C12.1325 3.33331 11.834 3.63179 11.834 3.99998V10.6666C11.834 11.0348 12.1325 11.3333 12.5007 11.3333C12.8688 11.3333 13.1673 11.0348 13.1673 10.6666V3.99998C13.1673 3.63179 12.8688 3.33331 12.5007 3.33331Z" fill="#777E91" />
        <path d="M7.16732 4.66665C6.79913 4.66665 6.50065 4.96512 6.50065 5.33331V10.6666C6.50065 11.0348 6.79913 11.3333 7.16732 11.3333C7.53551 11.3333 7.83398 11.0348 7.83398 10.6666V5.33331C7.83398 4.96512 7.53551 4.66665 7.16732 4.66665Z" fill="#777E91" />
        <path d="M4.50065 8.66665C4.13246 8.66665 3.83398 8.96512 3.83398 9.33331V10.6666C3.83398 11.0348 4.13246 11.3333 4.50065 11.3333C4.86884 11.3333 5.16732 11.0348 5.16732 10.6666V9.33331C5.16732 8.96512 4.86884 8.66665 4.50065 8.66665Z" fill="#777E91" />
        <path d="M9.16732 6.66665C9.16732 6.29846 9.46579 5.99998 9.83398 5.99998C10.2022 5.99998 10.5007 6.29846 10.5007 6.66665V10.6666C10.5007 11.0348 10.2022 11.3333 9.83398 11.3333C9.46579 11.3333 9.16732 11.0348 9.16732 10.6666V6.66665Z" fill="#777E91" />
      </svg>
    ),
    title: `24h Volume(${currentSymbolItem.value?.base})`,
    value: `${formatNumber(currentSymbolBalanceItem.value?.volume, 2)}`,
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M1.83268 2C2.20087 2 2.49935 2.29848 2.49935 2.66667V12C2.49935 12.3682 2.79783 12.6667 3.16602 12.6667H15.166C15.5342 12.6667 15.8327 12.9651 15.8327 13.3333C15.8327 13.7015 15.5342 14 15.166 14H3.16602C2.06145 14 1.16602 13.1046 1.16602 12V2.66667C1.16602 2.29848 1.46449 2 1.83268 2Z" fill="#777E91" />
        <path d="M12.5007 3.33331C12.1325 3.33331 11.834 3.63179 11.834 3.99998V10.6666C11.834 11.0348 12.1325 11.3333 12.5007 11.3333C12.8688 11.3333 13.1673 11.0348 13.1673 10.6666V3.99998C13.1673 3.63179 12.8688 3.33331 12.5007 3.33331Z" fill="#777E91" />
        <path d="M7.16732 4.66665C6.79913 4.66665 6.50065 4.96512 6.50065 5.33331V10.6666C6.50065 11.0348 6.79913 11.3333 7.16732 11.3333C7.53551 11.3333 7.83398 11.0348 7.83398 10.6666V5.33331C7.83398 4.96512 7.53551 4.66665 7.16732 4.66665Z" fill="#777E91" />
        <path d="M4.50065 8.66665C4.13246 8.66665 3.83398 8.96512 3.83398 9.33331V10.6666C3.83398 11.0348 4.13246 11.3333 4.50065 11.3333C4.86884 11.3333 5.16732 11.0348 5.16732 10.6666V9.33331C5.16732 8.96512 4.86884 8.66665 4.50065 8.66665Z" fill="#777E91" />
        <path d="M9.16732 6.66665C9.16732 6.29846 9.46579 5.99998 9.83398 5.99998C10.2022 5.99998 10.5007 6.29846 10.5007 6.66665V10.6666C10.5007 11.0348 10.2022 11.3333 9.83398 11.3333C9.46579 11.3333 9.16732 11.0348 9.16732 10.6666V6.66665Z" fill="#777E91" />
      </svg>
    ),
    title: `24h Turnover(${currentSymbolItem.value?.quote})`,
    value: `${formatNumber(currentSymbolBalanceItem.value?.quoteVolume, 2)}`,
  },
])

const tableColumns: Column[] = [
  {
    key: 'name',
    title: 'Name',
    minWidth: 200,
  },
  {
    key: 'price',
    title: 'Price',
    minWidth: 100,
    align: 'right',
  },
  {
    key: 'percent',
    title: '24h %',
    minWidth: 80,
    align: 'right',
  },
]

function onClickSymbol(row: SymbolItem) {
  showSymbolList.value = false
  changeCurrentSymbol(row.symbol)
}

function onClickOutside() {
  if (showSymbolList.value) {
    showSymbolList.value = false
    isClosed.value = true
    setTimeout(() => {
      isClosed.value = false
    }, 50)
  }
}

const symbolListFiltered = computed(() => symbolList.value.filter(i => i.symbol.toLowerCase().includes(search.value.toLowerCase()) && i.isSpotTradingAllowed))
</script>

<template>
  <div
    class="relative my-0.02 h-0.7 bg-black1 px-0.22"
  >
    <div class="h-0.7 flex items-center overflow-x-auto overflow-y-hidden py-0.1">
      <!-- asset name -->
      <div class="h-0.5 flex flex-none cursor-pointer items-center" @click="toggleSymbol()">
        <!-- <img :src="btcImg" class="mr-0.04 h-0.24 w-0.24"> -->
        <div class="flex items-center gap-x-0.04 text-0.24 font-600">
          <v-icon :currency="currentSymbolItem?.base" class="h-0.2d4 w-0.24" />
          <!-- <v-icon :currency="currentSymbolItem?.quote" class="h-0.24 w-0.24" /> -->
          {{ currentSymbolItem?.base || '-' }}/{{ currentSymbolItem?.quote || '-' }}
        </div>
        <div class="mx-0.12 h-0.32 w-0.32 flex-center border-2 border-grey1 rd-full">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M16.2071 9.79289C15.8166 9.40237 15.1834 9.40237 14.7929 9.79289L12 12.5858L9.20711 9.79289C8.81658 9.40237 8.18342 9.40237 7.79289 9.79289C7.40237 10.1834 7.40237 10.8166 7.79289 11.2071L11.2929 14.7071C11.6834 15.0976 12.3166 15.0976 12.7071 14.7071L16.2071 11.2071C16.5976 10.8166 16.5976 10.1834 16.2071 9.79289Z" fill="#777E91" />
          </svg>
        </div>
      </div>

      <!-- favorite -->
      <!-- <div class="cursor-pointer">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.52275 8.93361C9.42172 9.17558 9.19391 9.3408 8.93252 9.36167L4.18325 9.74094C3.56183 9.79057 3.31039 10.5666 3.78461 10.9712L7.39904 14.0554C7.59925 14.2262 7.68672 14.4949 7.62542 14.7509L6.52045 19.3652C6.37552 19.9704 7.03337 20.4497 7.56505 20.1262L11.6365 17.649C11.86 17.5131 12.1406 17.5131 12.3642 17.649L16.4356 20.1262C16.9672 20.4497 17.6251 19.9704 17.4802 19.3652L16.3752 14.7509C16.3139 14.4949 16.4014 14.2262 16.6016 14.0554L20.216 10.9712C20.6902 10.5666 20.4388 9.79057 19.8174 9.74094L15.0681 9.36167C14.8067 9.3408 14.5789 9.17558 14.4779 8.93361L12.6463 4.54699C12.4067 3.97327 11.5939 3.97327 11.3544 4.54699L9.52275 8.93361ZM16.0179 7.43115L14.4918 3.77638C13.5679 1.56346 10.4328 1.56348 9.50877 3.77638L7.98275 7.43115L4.02404 7.74729C1.62712 7.9387 0.65727 10.9318 2.48641 12.4926L5.49492 15.0597L4.57544 18.8994C4.0164 21.234 6.55383 23.0826 8.60461 21.8348L12.0003 19.7688L15.396 21.8348C17.4468 23.0826 19.9842 21.2339 19.4252 18.8994L18.5057 15.0597L21.5142 12.4926C23.3434 10.9318 22.3735 7.9387 19.9766 7.74729L16.0179 7.43115Z" fill="#777E91" />
        </svg>
      </div> -->

      <div class="flex flex-1 overflow-auto">
        <div class="ml-0.3 min-w-1.5 text-green">
          <div class="text-0.24 font-600" :class="percent24 > 0 ? 'text-green' : 'text-red'">
            {{ formatNumber(currentSymbolBalanceItem?.closePrice, currentPricePrecision) }}
          </div>
          <div class="text-0.12 font-600" :class="percent24 > 0 ? 'text-green' : 'text-red'">
            {{ percent24 }}%
          </div>
        </div>

        <div v-for="(item, index) in dataList" :key="item.title" class="data-item ml-0.24">
          <div class="flex-none">
            <div class="flex flex-none items-center">
              <Render :value="item.icon" />
              <div class="description">
                <div class="title font-400">
                  {{ item.title }}
                </div>
              </div>
            </div>
            <div class="value font-500" :class="item.valueClass">
              {{ item.value }}
            </div>
          </div>
          <div v-if="index !== dataList.length - 1" class="mx-0.24 h-0.48 bg-black2 pl-0.01" />
        </div>
      </div>
      <div v-if="isSigned" class="ml-0.1 flex-none cursor-pointer" @click="showPreference = true">
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M25.9065 17.3067C25.9598 16.88 25.9998 16.4533 25.9998 16C25.9998 15.5467 25.9598 15.12 25.9065 14.6933L28.7198 12.4933C28.9731 12.2933 29.0398 11.9333 28.8798 11.64L26.2131 7.02666C26.1345 6.88909 26.0095 6.78402 25.8605 6.73026C25.7114 6.67651 25.5481 6.6776 25.3998 6.73333L22.0798 8.06666C21.3865 7.53332 20.6398 7.09332 19.8265 6.75999L19.3198 3.22666C19.2977 3.06978 19.2192 2.92634 19.0989 2.82324C18.9786 2.72014 18.8249 2.66447 18.6665 2.66666H13.3331C12.9998 2.66666 12.7198 2.90666 12.6798 3.22666L12.1731 6.75999C11.3598 7.09332 10.6131 7.54666 9.91979 8.06666L6.59979 6.73333C6.52248 6.7071 6.44142 6.69359 6.35979 6.69332C6.13312 6.69332 5.90645 6.81332 5.78645 7.02666L3.11979 11.64C2.94645 11.9333 3.02645 12.2933 3.27979 12.4933L6.09312 14.6933C6.03979 15.12 5.99979 15.56 5.99979 16C5.99979 16.44 6.03979 16.88 6.09312 17.3067L3.27979 19.5067C3.02645 19.7067 2.95979 20.0667 3.11979 20.36L5.78645 24.9733C5.86505 25.1109 5.99008 25.216 6.13912 25.2697C6.28816 25.3235 6.45147 25.3224 6.59979 25.2667L9.91979 23.9333C10.6131 24.4667 11.3598 24.9067 12.1731 25.24L12.6798 28.7733C12.7198 29.0933 12.9998 29.3333 13.3331 29.3333H18.6665C18.9998 29.3333 19.2798 29.0933 19.3198 28.7733L19.8265 25.24C20.6398 24.9067 21.3865 24.4533 22.0798 23.9333L25.3998 25.2667C25.4798 25.2933 25.5598 25.3067 25.6398 25.3067C25.8665 25.3067 26.0931 25.1867 26.2131 24.9733L28.8798 20.36C29.0398 20.0667 28.9731 19.7067 28.7198 19.5067L25.9065 17.3067ZM23.2665 15.0267C23.3198 15.44 23.3331 15.72 23.3331 16C23.3331 16.28 23.3065 16.5733 23.2665 16.9733L23.0798 18.48L24.2665 19.4133L25.7065 20.5333L24.7731 22.1467L23.0798 21.4667L21.6931 20.9067L20.4931 21.8133C19.9198 22.24 19.3731 22.56 18.8265 22.7867L17.4131 23.36L17.1998 24.8667L16.9331 26.6667H15.0665L14.8131 24.8667L14.5998 23.36L13.1865 22.7867C12.6131 22.5467 12.0798 22.24 11.5465 21.84L10.3331 20.9067L8.91979 21.48L7.22645 22.16L6.29312 20.5467L7.73312 19.4267L8.91979 18.4933L8.73312 16.9867C8.69312 16.5733 8.66645 16.2667 8.66645 16C8.66645 15.7333 8.69312 15.4267 8.73312 15.0267L8.91979 13.52L7.73312 12.5867L6.29312 11.4667L7.22645 9.85332L8.91979 10.5333L10.3065 11.0933L11.5065 10.1867C12.0798 9.75999 12.6265 9.43999 13.1731 9.21332L14.5865 8.63999L14.7998 7.13332L15.0665 5.33332H16.9198L17.1731 7.13332L17.3865 8.63999L18.7998 9.21332C19.3731 9.45332 19.9065 9.75999 20.4398 10.16L21.6531 11.0933L23.0665 10.52L24.7598 9.83999L25.6931 11.4533L24.2665 12.5867L23.0798 13.52L23.2665 15.0267ZM15.9998 10.6667C13.0531 10.6667 10.6665 13.0533 10.6665 16C10.6665 18.9467 13.0531 21.3333 15.9998 21.3333C18.9465 21.3333 21.3331 18.9467 21.3331 16C21.3331 13.0533 18.9465 10.6667 15.9998 10.6667ZM15.9998 18.6667C14.5331 18.6667 13.3331 17.4667 13.3331 16C13.3331 14.5333 14.5331 13.3333 15.9998 13.3333C17.4665 13.3333 18.6665 14.5333 18.6665 16C18.6665 17.4667 17.4665 18.6667 15.9998 18.6667Z" fill="#777E91" />
        </svg>
      </div>
    </div>
    <Transition name="modal">
      <div v-if="showSymbolList" class="mask absolute bottom--10 left-0 right-0 top-0.7 z-200">
        <div
          v-if="showSymbolList"
          v-on-click-outside="onClickOutside"
          class="absolute left-0 top-0 z-201 max-h-5 w-5.4 flex flex-col overflow-auto rd-0.16 bg-#18191D p-0.2"
        >
          <!-- <v-input v-model="search" align="left" class="flex-none mb-0.08 ">
              <template #suffix>
                <div class="h-0.18 w-0.18 flex-center cursor-pointer border-1 border-black3 rd-full" @click="clearSearch">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M2.64645 2.64645C2.84171 2.45118 3.15829 2.45118 3.35355 2.64645L6 5.29289L8.64645 2.64645C8.84171 2.45118 9.15829 2.45118 9.35355 2.64645C9.54882 2.84171 9.54882 3.15829 9.35355 3.35355L6.70711 6L9.35355 8.64645C9.54882 8.84171 9.54882 9.15829 9.35355 9.35355C9.15829 9.54882 8.84171 9.54882 8.64645 9.35355L6 6.70711L3.35355 9.35355C3.15829 9.54882 2.84171 9.54882 2.64645 9.35355C2.45118 9.15829 2.45118 8.84171 2.64645 8.64645L5.29289 6L2.64645 3.35355C2.45118 3.15829 2.45118 2.84171 2.64645 2.64645Z" fill="#FCFCFD" />
                  </svg>
                </div>
              </template>
            </v-input> -->
          <v-table
            :data="symbolListFiltered"
            :columns="tableColumns"
            :column-gap="35"
            class="flex-1"
            title-row-class="leading-0.24 px-0.16 py-0.08 border-b border-black2"
            content-class="mt--0.08"
            row-class="h-0.48 flex items-center gap-y-0 hover:bg-black2 px-0.16 py-0.1 cursor-pointer rd-0.04"
            @click-row="onClickSymbol"
          >
            <template #percent="{ data: { row } }">
              <div v-if="+row.openPrice" :class="+symbolPrice[row.symbol]?.closePrice < +symbolPrice[row.symbol]?.openPrice ? 'text-red' : 'text-green'">
                {{ formatNumber((+symbolPrice[row.symbol]?.closePrice - (+symbolPrice[row.symbol]?.openPrice)) / +symbolPrice[row.symbol]?.openPrice * 100) }} %
              </div>
              <div v-else>
                -
              </div>
            </template>
            <template #price="{ data: { row } }">
              <div v-if="!+symbolPrice[row.symbol]?.closePrice">
                -
              </div>
              <div v-else class="text-0.14 font-500" :class="+symbolPrice[row.symbol]?.closePrice < +symbolPrice[row.symbol]?.openPrice ? 'text-red' : 'text-green'">
                {{ formatNumber(symbolPrice[row.symbol]?.closePrice, getPrecisionFromSymbol(row.symbol).price) }}
              </div>
            </template>
            <template #name="{ data: { row } }">
              <div class="flex items-center text-0.14 font-500 text-white2">
                <!-- <v-icon :currency="row?.base" class="h-0.24 w-0.24" />
                  <v-icon :currency="row?.quote" class="h-0.24 w-0.24" /> -->
                <span>{{ row.base }}</span><span class="text-grey1">/{{ row.quote }}</span>
              </div>
            </template>
          </v-table>
        </div>
      </div>
    </Transition>

    <n-drawer v-if="Object.keys(preference || {})?.length > 0" v-model:show="showPreference" :width="458" placement="right">
      <n-drawer-content>
        <div class="flex items-center justify-between">
          <div class="text-0.32 font-700 font-dm text-white2">
            Preference Setting
          </div>
          <div class="h-0.4 w-0.4 flex-center flex-none cursor-pointer border-2 border-black3 rd-full transition-all hover:rotate-90" @click="showPreference = false">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L12 10.5858L17.2929 5.29289C17.6834 4.90237 18.3166 4.90237 18.7071 5.29289C19.0976 5.68342 19.0976 6.31658 18.7071 6.70711L13.4142 12L18.7071 17.2929C19.0976 17.6834 19.0976 18.3166 18.7071 18.7071C18.3166 19.0976 17.6834 19.0976 17.2929 18.7071L12 13.4142L6.70711 18.7071C6.31658 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6834 5.29289 17.2929L10.5858 12L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289Z" fill="#FCFCFD" />
            </svg>
          </div>
        </div>
        <div class="mt-0.32 flex flex-col gap-y-0.32">
          <div class="flex justify-between">
            <div>Order Confirmation</div>
            <n-switch v-model:value="preference.orderConfirmation" @update:value="updatePreference" />
          </div>
          <div class="flex justify-between">
            <div>‘Cancel All’ Confirmation</div>
            <n-switch v-model:value="preference.cancelAllConfirmation" @update:value="updatePreference" />
          </div>
          <div class="flex justify-between">
            <div class="flex items-center gap-x-0.04">
              Trade Notification
              <n-popover trigger="hover" :max-width="200">
                <template #trigger>
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none" class="cursor-pointer">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M10.6662 17.7777C14.5936 17.7777 17.7773 14.594 17.7773 10.6666C17.7773 6.73924 14.5936 3.55549 10.6662 3.55549C6.73887 3.55549 3.55512 6.73924 3.55512 10.6666C3.55512 14.594 6.73887 17.7777 10.6662 17.7777ZM10.6662 19.5555C15.5754 19.5555 19.5551 15.5758 19.5551 10.6666C19.5551 5.7574 15.5754 1.77771 10.6662 1.77771C5.75704 1.77771 1.77734 5.7574 1.77734 10.6666C1.77734 15.5758 5.75704 19.5555 10.6662 19.5555Z" fill="#777E91" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M10.6662 6.22229C10.1753 6.22229 9.77734 6.62026 9.77734 7.11118C9.77734 7.6021 10.1753 8.00007 10.6662 8.00007C11.1572 8.00007 11.5551 7.6021 11.5551 7.11118C11.5551 6.62026 11.1572 6.22229 10.6662 6.22229ZM10.6662 9.77785C10.1753 9.77785 9.77734 10.1758 9.77734 10.6667V14.2223C9.77734 14.7132 10.1753 15.1112 10.6662 15.1112C11.1572 15.1112 11.5551 14.7132 11.5551 14.2223V10.6667C11.5551 10.1758 11.1572 9.77785 10.6662 9.77785Z" fill="#777E91" />
                  </svg>
                </template>
                <span class="font-poppins">When enabled, you will see a prompt when the order is placed, canceled, filled, partially filled, or expired.</span>
              </n-popover>
            </div>
            <n-switch v-model:value="preference.tradeNotification" @update:value="updatePreference" />
          </div>
          <!-- <div class="flex justify-between">
            <div>Click Depth to Input Amount</div>
            <n-switch v-model:value="preference.clickDepthInputAmount" @change="updatePreference" />
          </div>
          <div class="flex justify-between">
            <div>24h Price Time</div>
            <n-switch v-model:value="preference.cancelAllConfirmation" @change="updatePreference" />
          </div> -->
        </div>
      </n-drawer-content>
    </n-drawer>
  </div>
</template>

<style scoped lang="scss">
.data-item {
  display: flex;
  .description {
    display: flex;
    align-items: center;
    color: var(--vessel-color-grey-1);
    .title {
      margin-left: calc(4rem / 400);
      font-size: calc(12rem / 400);
      line-height: calc(20rem / 400);
    }
  }
  .value {
    margin-top: calc(4rem / 400);
    min-width: calc(118rem / 400);
  }
}

.modal-enter-active,
.modal-leave-active {
  transition: all 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.mask {
  background: rgba(20, 20, 22, 0.30);
  backdrop-filter: blur(1.5px);
}
</style>
